import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { impossible } from "../../app/Utils";
import { commentsSelectors, selectDocument } from "./documentSlice";
import { useCallback, useEffect, useRef, useState } from "react";
import { unwrapResult } from "@reduxjs/toolkit";
import { loadRiskAnalysisContent, RiskAnalysisContent } from "./riskAnalysis/riskAnalysisApi";
import { ErrorContainer } from "../../app/ErrorContainer";
import { LoadingContainer } from "../../app/LoadingContainer";
import { DataSetterContext, RiskAnalysisForm as Content } from "../riskAnalysis/NewRisk";
import produce from "immer";
import { loadDocumentAllTests } from "./documentTests/documentTestsApi";
import { SimpleRiskAnalysisForm } from "../riskAnalysis/SimpleRiskAnalysis";
import { If } from "../../app/If";
import { useLocation } from "react-router-dom";

export interface RiskAnalysisSectionProps {
    sectionId: string
}
export const RiskAnalysisSection = (props: RiskAnalysisSectionProps) => {
    const { sectionId } = props
    const { header } = useAppSelector(selectDocument)?.doc ?? impossible()
    const { id } = header
    const dispatch = useAppDispatch()
    const { search } = useLocation()
    const testsLoaded = useAppSelector(state => selectDocument(state).doc?.tests.loaded ?? false)
    const comments = useAppSelector(commentsSelectors.selectAll)
    const [document, setDocument] = useState<RiskAnalysisContent | undefined>(undefined);

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [error, setError] = useState<Error | null>(null);

    // Read the flag inside the effect without making the effect depend on it:
    // loading the tests flips it to true, which would otherwise re-run the fetch.
    const testsLoadedRef = useRef(testsLoaded)
    testsLoadedRef.current = testsLoaded

    useEffect(() => {
        let cancelled = false

        const fetchData = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const content = await dispatch(loadRiskAnalysisContent(id)).then(unwrapResult);
                if (cancelled) {
                    return
                }
                setDocument(content);

                if (!testsLoadedRef.current) {
                    await dispatch(loadDocumentAllTests({
                        documentId: Number.parseInt(id),
                    })).then(unwrapResult)
                }

            } catch (e) {
                if (cancelled) {
                    return
                }
                setError(e instanceof Error ? e : new Error('An unknown error occurred'));
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            cancelled = true
        }
    }, [dispatch, id]);

    const updateDocLocally = useCallback((f: (data: RiskAnalysisContent) => void) => {
        setDocument(produce(draft => {
            draft && f(draft)
        }))
    }, [])

    // Every risk analysis — including those generated from a URS — now renders the
    // unified SimpleRiskAnalysisForm. The legacy NewRisk view is kept reachable at
    // ?raView=legacy so it can still be compared against on production; once the
    // new views are confirmed there, both this flag and NewRisk.tsx come out.
    const useLegacyView = new URLSearchParams(search).get('raView') === 'legacy'

    if (error) {
        return <>
            <ErrorContainer error={error} />
        </>
    }

    if (isLoading) {
        return <>
            <LoadingContainer />
        </>
    }

    if (document) {
        return <DataSetterContext.Provider value={updateDocLocally}>
            <If
                condition={useLegacyView}
                otherwise={<SimpleRiskAnalysisForm doc={document} sectionId={sectionId} comments={comments} />}
            >
                <Content doc={document} sectionId={sectionId} comments={comments} />
            </If>
        </DataSetterContext.Provider>
    }

    return <div>RA</div>
}
